import querystring from 'qs';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { paramConverters, yiluEnv, yiluTracking } from 'utils';

const defineGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
};

const loadScript = (src: string, onLoad?: () => void) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  if (onLoad) script.onload = onLoad;
  document.body.appendChild(script);
};

const initializeGTM = (id: string, setReady: () => void) => {
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${id}`, setReady);
};

const initializeGA = (id: string, setReady: () => void) => {
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${id}`, () => {
    window.gtag('js', new Date());
    setReady();
  });
};

const sendPageView = (
  id: string,
  pathname: string,
  search: string,
  tracking: any,
  touchpoint: string | undefined,
) => {
  const trackingAllowed = tracking?.allowed === 'true';
  const pageLocation = `${
    window.location.origin
  }${pathname}${paramConverters.omitExternalQueryParameters(search)}`;

  window.gtag('config', id, {
    page_location: pageLocation,
    page_path: pathname,
    anonymize_ip: !trackingAllowed,
    ...(touchpoint ? { touchpoint } : {}),
  });
};

const enhanceGtagWithTouchpoint = (touchpoint: string) => {
  const originalGtag = window.gtag;
  window.gtag = (
    command: 'config' | 'event' | 'set' | 'js',
    identifier: string,
    params: any = {},
  ) => {
    if (command === 'event') {
      params = { ...params, touchpoint };
    }
    originalGtag(command, identifier, params);
  };
};

const Analytics: React.FC = () => {
  const { search, pathname } = useLocation();
  const yiluEnvVariables = yiluEnv.getVariables();

  const googleTagManagerId = yiluEnvVariables.GOOGLE_TAG_MANAGER_CONTAINER_ID;
  const googleAnalyticsId = yiluEnvVariables.GOOGLE_ANALYTICS_MEASUREMENT_ID;

  const { tracking, touchpoint }: any = querystring.parse(search, { ignoreQueryPrefix: true });

  const [isGTMReady, setIsGTMReady] = useState(false);
  const [isGAReady, setIsGAReady] = useState(false);

  // redefine in case it was previously deleted from window
  defineGtag();

  useEffect(() => {
    if (googleTagManagerId && !isGTMReady) {
      initializeGTM(googleTagManagerId, () => setIsGTMReady(true));
    }
  }, [googleTagManagerId, isGTMReady]);

  useEffect(() => {
    if (googleAnalyticsId && !isGAReady) {
      initializeGA(googleAnalyticsId, () => setIsGAReady(true));
    }
  }, [googleAnalyticsId, isGAReady]);

  useEffect(() => {
    if (googleAnalyticsId) {
      sendPageView(googleAnalyticsId, pathname, search, tracking, touchpoint);
    }
  }, [pathname, search, googleAnalyticsId, tracking, touchpoint]);

  useEffect(() => {
    if (googleAnalyticsId) {
      yiluTracking.sendGAEvent({
        event: 'first_page_load',
        category: 'page_load',
        label: 'first_load_success',
      });
    }
  }, [googleAnalyticsId]);

  useEffect(() => {
    if (touchpoint) {
      enhanceGtagWithTouchpoint(touchpoint);
    }
  }, [touchpoint]);

  return null;
};

export default Analytics;
